/**
 * EndUserAppView — the chat-pane-agnostic, container-filling end-user view
 * of an application.
 *
 * Used by:
 *   - RunPage (full-viewport, full-tab end-user preview)
 *   - Builder Mode's PreviewFrame (split-pane right side)
 *
 * The navigation LAYOUT (sidebar tree + object pane) is the SHARED
 * `RunNavShell` from @twasta/page-runtime — the same component the deployed
 * runtimes mount — so the platform Run view and a deployed app look and
 * behave identically. This file wires the platform-specific parts around it:
 * data loading (application.json via /api/generateapp), the designer
 * LivePreview panes as the object renderer, the Run top bar, builder-mode
 * gating and the Assistant launcher.
 *
 * Fills its parent container (100% width/height) rather than the viewport,
 * so it can be embedded without layout conflicts. Hot-reloads on the
 * `twasta:agent-files-changed` event so AI-driven changes appear without
 * a manual refresh.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ExternalLink,
  FileText,
  Globe,
  Loader2,
  RefreshCw,
  Save,
  Wand2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { RunNavShell, RUN_TYPE_ICON, RUN_TYPE_LABEL, type RunNavItem } from '@twasta/page-runtime/RunNavShell'
import { filterNavByRoles } from '@twasta/page-runtime/navFilter'
import { AppSearch, hitTarget, type GlobalSearchHit, type OpenTarget, type SearchScreen } from '@twasta/page-runtime/search'
import { ProcessOverview, processFlows } from '@twasta/page-runtime/txn/flow'
import {
  openScreenPayloadOf, readOpen, stashOpen, type OpenActionRequest, type WizardActionRequest,
} from '@twasta/page-runtime/txn'

import {
  StagingBanner, SupportWorkspace, installErrorRing, supportTabOf, type SupportMeta,
} from '@twasta/page-runtime/support'
import { SUPPORT_NAV_ID } from '@twasta/page-runtime/search'

/** The Process Overview's synthetic nav id — not an authored object, so it is
 *  reserved rather than looked up. */
const PROCESS_OVERVIEW_ID = '__process_overview'
/** The Support module's synthetic nav entry — the board, releases, versioning
 *  and settings, offered to the application's support team only. */
const SUPPORT_ID = SUPPORT_NAV_ID
const SUPPORT_TYPE = 'SUPPORT'
import { globalSearch } from '@/api/appsearch'
import { getJsonRaw, saveJson, checkModelStatus } from '@/api/generateapp'
import { livePreviewExecute, livePreviewLoad, worklistSeed } from '@/livepreview/api'
import { loadModel } from '@/livepreview/runtime/loadModel'
import { getProjects } from '@/api/workbench'
import { siteRunIdentity } from '@/api/website'
import { runAppIdentity } from '@/api/runapp'
import { runSessionHeaders } from '@/api/runSession'
import { startActivity } from '@/activity/hostTracker'
import { siteAddress } from '@/config/hostBinding'
import { useBuilderContextStore } from '@/store/builderContextStore'
import LivePreviewLazyPane from '@/pages/livepreview/LivePreviewPage'
import DocumentFormPane from './DocumentFormPane'
import WizardPane, { wizardTarget } from './WizardPane'
import SmartPageLivePreviewPane from '@/pages/workbench/smartpage-preview/SmartPageLivePreviewPane'
import { GenProcessLivePreviewPane } from '@/pages/designers/genprocess/RunPanel'
import { SchedulerRunPane } from '@twasta/page-runtime/shell/jobs'
import { makeSchedulerRuntime } from '@/pages/designers/scheduler/runtime'
import FlowRunPane from '@/pages/designers/scheduler/FlowRunPane'
import { VisualLivePreviewPane } from '@/pages/designers/visual/VisualPreviewModal'
import { DashboardLivePreviewPane } from '@/pages/designers/dashboard/DashboardPreviewModal'
import { ReportLivePreviewPane } from '@/pages/designers/report/ReportLivePreviewPane'
import { DocumentRepositoryPane } from '@/pages/designers/document/DocumentRunPane'
import type { FlatNavItem, RawAppData, User } from '@/types'
import RunTopBar from './RunTopBar'
import { makeSupportRuntime } from '@/api/support'
import { APP_BASE } from '@/config/basePath'
import { platformNotify } from '@/components/runtime-shell'
import { AssistantLauncher } from '@/assistant/AssistantLauncher'
import { ShellChromeProvider, type AppShellSettings } from '@twasta/page-runtime/shell'
import { makeShellPrefsTransport } from '@/api/userMemory'

// User-facing noun for the "Create …" button in Builder Mode. A Transaction is
// presented as a "Form" since that's what an end user actually sees.
const CREATE_NOUN: Record<string, string> = {
  T: 'Form',
  S: 'Page',
  V: 'Visual',
  D: 'Dashboard',
  R: 'Report',
  G: 'Process',
  U: 'Wizard',
}

interface EndUserAppViewProps {
  project: string
  /** Variant changes the header chrome:
   *  - 'run'     → "END-USER PREVIEW" subtitle (full-page Run view)
   *  - 'builder' → slimmer header, "LIVE PREVIEW" subtitle, builder accent
   */
  variant?: 'run' | 'builder'
  /** Compact mode (smaller nav width, smaller header) — used inside split panes. */
  compact?: boolean
  /** Project owner's user id — enables the project-specific user menu in the
   *  run top bar (site-user store is keyed by owner + project). Run view only. */
  ownerUserId?: string
  /** The logged-in Twasta platform user. In the in-app Run view this is the
   *  actual acting identity (data calls go through /api/livepreview with this
   *  user's cookie), so the top bar shows it as authenticated by default —
   *  unless a separate project site-user session is explicitly established. */
  platformUser?: User | null
  /** True when the application is being run by a PROJECT USER — somebody
   *  invited from its own System administration screen, who holds no Twasta
   *  account. Their identity, roles and object rights come from the tenant
   *  registry rather than the platform, so the identity call differs; the
   *  application itself is rendered identically. */
  projectUser?: boolean
  /** Serving exactly one application (a bound domain) — hides the app
   *  switcher, whose destinations do not exist on that host. */
  singleApp?: boolean
  /** Offer "sign in to this application" when the acting Twasta account is
   *  refused it. Omitted when the application does not take project users, in
   *  which case there is nothing to offer. */
  onNeedsProjectSignIn?: () => void
  /** Object (obj_name) to open on mount, from the URL splat. Drives deep
   *  linking when paired with onObjectChange. */
  initialObjName?: string
  /** When provided, the view is URL-driven: selecting an object calls this so
   *  the host (RunPage) reflects it in the address bar, and `selected` is
   *  restored from `initialObjName`. Omitted in builder/compact embeds, which
   *  must not touch the URL. */
  onObjectChange?: (objName: string | null, opts?: { replace?: boolean }) => void
  /** In-screen state for the open object, parsed from the URL query (e.g.
   *  an open transaction record). Forwarded to the object's preview pane. */
  screenState?: Record<string, string>
  /** Push new in-screen state to the URL query (keeps the current object). */
  onScreenStateChange?: (next: Record<string, string> | null) => void
  /**
   * Open a DIFFERENT object with in-screen state in one navigation — what a
   * search result needs and neither `onObjectChange` (which clears the query)
   * nor `onScreenStateChange` (which keeps the current object) can express.
   * Supplied by RunPage, which owns the URL shape; absent → no search button,
   * because a search whose results cannot be opened is not a feature.
   */
  onOpenTarget?: (target: { objName: string; params: Record<string, string> }) => void
  /** Embedded in a host application (e.g. opened from Proteus Vision in an
   *  iframe): render ONLY the requested object — no run top bar, no navigation
   *  tree, no object header. The host already supplies its own menu and header,
   *  and showing ours beside them gives the user two of each. Ignored when the
   *  URL names no object: there would be nothing left to draw. */
  embedded?: boolean
  /** The release-management environment this view was opened on
   *  (`stage:<n>` from QC's "Open staging") — drives the staging banner. */
  runEnv?: string | null
  /** Leave that environment (RunPage drops it and reloads). */
  onExitEnv?: () => void
}

/**
 * Where QC's "Open staging" goes: this same Run view, on the fix's staging
 * copy. Built from the CURRENT path (`/run/:tenant/:project`) for the reason
 * RunTopBar gives — the tenant segment cannot be rebuilt from props — and the
 * bare root on an application's own domain, where the app IS the site.
 */
function stagingUrl(issueNumber: number): string {
  const parts = window.location.pathname.split('/').filter(Boolean)
  const runIdx = parts.indexOf('run')
  const base = runIdx >= 0 && parts.length >= runIdx + 3
    ? `/${parts.slice(0, runIdx + 3).join('/')}`
    : `${APP_BASE}/`
  return `${base}?env=${encodeURIComponent(`stage:${issueNumber}`)}`
}

function openStaging(issueNumber: number): void {
  window.open(stagingUrl(issueNumber), '_blank', 'noopener')
}

export default function EndUserAppView({
  project,
  variant = 'run',
  compact = false,
  ownerUserId,
  platformUser,
  projectUser = false,
  singleApp = false,
  onNeedsProjectSignIn,
  initialObjName,
  onObjectChange,
  embedded = false,
  screenState,
  onScreenStateChange,
  onOpenTarget,
  runEnv,
  onExitEnv,
}: EndUserAppViewProps) {
  // Application-wide search — records + document content in one query, opened
  // from the run top bar or Ctrl/Cmd+K. Run view only: the builder preview
  // pane draws no top bar and does not own the URL.
  const [searchOpen, setSearchOpen] = useState(false)
  // Whether this application defines a Process Flow, and so whether the
  // Process Overview entry belongs in the menu. One cheap call per Run
  // session; an app with no flows never shows the entry.
  const [hasFlows, setHasFlows] = useState(false)
  const [data, setData] = useState<RawAppData | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Access outcomes are kept apart from `loadError`: a 403/404 is a statement
  // about the signed-in account's rights, not a broken request, and rendering
  // it as "Request failed with status code 404" tells the user nothing they
  // can act on.
  const [accessError, setAccessError] = useState<'forbidden' | 'not-available' | 'no-projects' | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  // Mirror of the shell's open object, for builder-context publishing.
  const [openItem, setOpenItem] = useState<RunNavItem | null>(null)
  // The caller's functional roles on this project (from the tenant data DB) —
  // drives rights-filtered navigation in the Run view. `null` = not resolved
  // (yet, or the identity call failed) → show the full nav (filtering is UX
  // only; object-level rights remain the security boundary).
  const [runRoles, setRunRoles] = useState<string[] | null>(null)
  // The caller sees the WHOLE nav tree, unfiltered — true for a runtime
  // administrator (project owner / FUNC-ADMIN) and for any member who BUILDS
  // this application (`authors_app`: a PROJECT_MANAGER, ANALYST, DEVELOPER, …).
  // A nav node's `roles` allow-list describes END USERS of the app; the people
  // building it usually hold no functional role at all, so without this they
  // open Run and find every role-gated screen missing while reaching the same
  // objects freely from the designer. Mirrors the object-rights gate, which
  // already exempts an author (`runtime_rights.authors_the_app`).
  const [runSeesFullNav, setRunSeesFullNav] = useState(false)
  // Objects this caller holds no OBJECT GRANT on. A nav node's `roles`
  // allow-list and the object grant are two independent lists, and only the
  // grant is editable from inside the running app (System administration →
  // Access) — so without this, removing a role's grant stopped the data
  // loading but left the screen in their menu, opening to a permission error.
  const [runDeniedObjects, setRunDeniedObjects] = useState<string[]>([])
  // Objects a grant DOES give them. The node's `roles` list is a snapshot taken
  // when the app was generated, so a role created later can never be in it —
  // without this, granting a new role a screen works everywhere but the menu.
  const [runGrantedObjects, setRunGrantedObjects] = useState<string[]>([])
  // DENY BY DEFAULT: the server says whether this caller is subject to the
  // object gate at all. When they are, a node whose object no grant gives them
  // is hidden — not just one that other roles are granted — which is what the
  // endpoints enforce. False for an administrator or an author.
  const [runRequireGrant, setRunRequireGrant] = useState(false)
  // Who the Run shell keeps layout / Pinned / Recent / open-editor drafts for
  // on THIS device (the server record is keyed by the session itself).
  const [runUserKey, setRunUserKey] = useState('')
  const shellPrefs = useMemo(
    () => (variant === 'run' && !embedded ? makeShellPrefsTransport(project) : undefined),
    [variant, embedded, project],
  )
  // The Support module — one transport for the whole view, and the caller's
  // support identity. `null` until known (and on failure): no button, no board.
  const supportRuntime = useMemo(() => makeSupportRuntime(project), [project])
  const [supportMeta, setSupportMeta] = useState<SupportMeta | null>(null)

  const load = useCallback(() => {
    if (!project) return
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    setAccessError(null)
    getJsonRaw(project)
      .then((raw: RawAppData) => {
        if (cancelled) return
        setData(raw)
      })
      .catch(async (err: unknown) => {
        if (cancelled) return
        const status = (err as { response?: { status?: number } })?.response?.status
        // 403/404 are not failures to report as such — they are answers about
        // WHO is asking. `resolve_access` returns 404 both for "no such
        // project" and "not shared with this account", so the message must
        // cover both without claiming the app doesn't exist. Which of the two
        // it is depends on whether this account can see anything at all, so
        // ask (only on this path — it costs one request in an error state).
        if (status === 403 || status === 404) {
          let hasAnyProject: boolean | null = null
          try {
            hasAnyProject = (await getProjects()).length > 0
          } catch {
            // Leave it null — the message below then avoids asserting either way.
          }
          if (cancelled) return
          setAccessError(status === 403 ? 'forbidden' : (hasAnyProject === false ? 'no-projects' : 'not-available'))
          return
        }
        const msg = err instanceof Error ? err.message : String(err)
        setLoadError(msg || 'Failed to load application')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // `projectUser` is a dependency because it changes WHO is asking, not just
    // what is asked for: signing in as a project user makes the very request
    // that just answered 404 answer 200. Without it the view kept showing the
    // refusal it had already been granted past.
  }, [project, projectUser])

  useEffect(() => {
    const cleanup = load()
    return cleanup
  }, [load, reloadKey])

  // Hot-reload when the agent finishes writing files.
  useEffect(() => {
    const onFilesChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail as { project?: string } | undefined
      if (!detail || !detail.project || detail.project === project) {
        setReloadKey((k) => k + 1)
      }
    }
    window.addEventListener('twasta:agent-files-changed', onFilesChanged as EventListener)
    return () =>
      window.removeEventListener('twasta:agent-files-changed', onFilesChanged as EventListener)
  }, [project])

  // Publish the open object so the Ask-AI dock can resolve "this" to it.
  // Builder Mode only — the Run view has no agent dock. Cleared on
  // deselect/unmount so a stale screen never leaks into a later request.
  useEffect(() => {
    if (variant !== 'builder') return
    const { setOpenObject } = useBuilderContextStore.getState()
    if (openItem && openItem.obj_type !== '-') {
      setOpenObject({
        obj_name: openItem.obj_name,
        obj_type: openItem.obj_type,
        title: openItem.title,
      })
    } else {
      setOpenObject(null)
    }
    return () => useBuilderContextStore.getState().setOpenObject(null)
  }, [variant, openItem])

  // Resolve the Twasta caller's functional roles for rights-filtered nav —
  // Run view only (Builder Mode must show every node so the designer can
  // manage them). Same endpoint RunTopBar uses for the admin gate.
  useEffect(() => {
    if (variant !== 'run' || !project) return
    if (!projectUser && !ownerUserId) return
    let cancelled = false
    // Two callers, two identity endpoints, one answer shape: a Twasta account
    // is resolved from its platform cookie, a project user from the registry
    // session they signed into the application with. Both report roles and the
    // objects their grants hide, so the nav filter below reads one field set.
    const identity = projectUser
      ? runAppIdentity(project)
      : siteRunIdentity(ownerUserId as string, project)
    identity
      .then((r) => {
        if (cancelled) return
        setRunRoles(r.data.roles || [])
        setRunSeesFullNav(!!r.data.is_admin || !!r.data.authors_app)
        setRunDeniedObjects(r.data.denied_objects || [])
        setRunGrantedObjects(r.data.granted_objects || [])
        setRunRequireGrant(!!r.data.require_grant)
        const who = r.data as { email?: string | null; username?: string | null }
        setRunUserKey(String(who.email || who.username || '').toLowerCase())
      })
      .catch(() => {
        // FAIL CLOSED, to match the endpoints. Leaving the nav unfiltered here
        // would offer every screen in the app to a caller we could not identify
        // — and since the data endpoints deny on the same failure, every one of
        // those entries opens to a 403. An empty menu is the honest rendering;
        // a reload recovers it once the identity call succeeds.
        if (cancelled) return
        setRunRoles([])
        setRunSeesFullNav(false)
        setRunDeniedObjects([])
        setRunGrantedObjects([])
        setRunRequireGrant(true)
      })
    return () => { cancelled = true }
  }, [variant, ownerUserId, project, projectUser])

  // Being refused is not the answer when there IS another way in. If this
  // account cannot open the application but the application takes its own
  // users, offer that sign-in straight away rather than leaving a refusal on
  // screen with the remedy hidden behind a button — the refusal is what the
  // person reads, and it tells them to go and ask somebody.
  //
  // Once per refusal: closing the sign-in leaves the panel (and its button) as
  // the way back, and must not immediately reopen it.
  // WHERE THERE IS A WAY IN, OFFER IT RATHER THAN A REFUSAL.
  //
  // Reaching this means the Twasta account cannot open the application. That is
  // a statement about THAT account, and for anyone who also holds an account IN
  // the application it is not the end of the road — most plainly the person who
  // has just signed out of one, for whom "ask the owner to share it with you"
  // is both wrong and unactionable when the sign-in they want is one click away.
  //
  // Never while already acting as a project user: a refusal there is about
  // RIGHTS, not identity, and re-asking for the same password would answer
  // nothing and loop.
  useEffect(() => {
    if (projectUser || !accessError || !onNeedsProjectSignIn) return
    onNeedsProjectSignIn()
  }, [accessError, onNeedsProjectSignIn, projectUser])

  // Support: who this caller is to the application's support pipeline. Run
  // view only — the builder preview is the builders' own surface, and its
  // Support tab lives in the workbench. A failure hides the module rather
  // than breaking the application around it.
  useEffect(() => {
    if (variant !== 'run' || !project || embedded) { setSupportMeta(null); return undefined }
    let cancelled = false
    supportRuntime.meta()
      .then((m) => { if (!cancelled) setSupportMeta(m) })
      .catch((err) => {
        if (cancelled) return
        setSupportMeta(null)
        console.warn('[support] meta unavailable for', project, err)
      })
    return () => { cancelled = true }
  }, [variant, project, embedded, supportRuntime, projectUser])

  // The client error ring feeds every issue report's automatic context. Once
  // per page; installing again is a no-op.
  useEffect(() => { if (variant === 'run') installErrorRing() }, [variant])

  // Activity analytics: this application's users — screen views come from
  // the shared RunNavShell, saves/actions from the shared transaction screens.
  // A project user is named by their run-session headers; a Twasta account by
  // its cookie. The server resolves who it is; the browser never says.
  useEffect(() => {
    if (variant !== 'run' || embedded) return
    startActivity({
      realm: 'app', surface: 'run', owner: ownerUserId || '', project,
      headers: () => (projectUser ? runSessionHeaders() : {}),
    })
  }, [variant, embedded, ownerUserId, project, projectUser])

  const handleSelect = useCallback((item: RunNavItem | null, meta?: { auto?: boolean }) => {
    setOpenItem(item)
    // An automatic open (the Home screen on load) replaces the entry instead of
    // stacking it, so Back leaves the application rather than bouncing off it.
    onObjectChange?.(item ? item.obj_name : null, meta?.auto ? { replace: true } : undefined)
  }, [onObjectChange])

  useEffect(() => {
    if (variant !== 'run') return undefined
    let cancelled = false
    processFlows(
      (objName, action, payload) => livePreviewExecute(project, objName, action, payload),
      '',
    )
      .then((res) => { if (!cancelled) setHasFlows((res.flows || []).length > 0) })
      .catch(() => { if (!cancelled) setHasFlows(false) })
    return () => { cancelled = true }
  }, [project, variant])

  const runSearch = useCallback(
    (request: Parameters<typeof globalSearch>[1], signal?: AbortSignal) =>
      globalSearch(project, request, signal),
    [project],
  )

  /**
   * Open one search result, or one Assistant citation.
   *
   * Both are ordinary Run-view deep links — the same URLs a reader could have
   * bookmarked — so a result, a citation and a shared link land on the same
   * screen in the same state. WHICH url is `openTarget`'s decision, shared with
   * the Assistant so "open the record this answer came from" cannot drift from
   * "open this search hit"; this host only knows how to navigate.
   */
  const openHit = useCallback((hit: GlobalSearchHit) => {
    const target = hitTarget(hit)
    if (target) onOpenTarget?.(target)
  }, [onOpenTarget])

  const openTarget = useCallback((target: OpenTarget) => {
    onOpenTarget?.(target)
  }, [onOpenTarget])

  if (loading && !data) {
    return (
      <FillCenter>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
        <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>Loading…</span>
      </FillCenter>
    )
  }

  if (accessError) {
    // Always name the acting account. When the app is opened through SSO from
    // a host application, the user has no other way to tell WHICH identity the
    // session resolved to — and that is usually the whole answer.
    const who = platformUser?.email
    const title = accessError === 'forbidden'
      ? 'You don\'t have access to this application'
      : accessError === 'no-projects'
        ? 'No applications available'
        : `"${project}" isn't available to this account`
    const body = accessError === 'forbidden'
      ? `This account is signed in but has no permission to open "${project}". Ask the application's owner to grant you access.`
      : accessError === 'no-projects'
        ? 'This account has no applications yet, and none have been shared with it.'
        : onNeedsProjectSignIn
          ? 'This account has not been given the application. If its administrator gave you an account inside it, sign in with that.'
          : 'It may not exist, or it hasn\'t been shared with this account. Ask the application\'s owner to share it with you.'
    return (
      <FillCenter>
        <div style={{ maxWidth: 380, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>{title}</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{body}</div>
          {who && (
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              Signed in as <strong style={{ color: 'var(--color-text)' }}>{who}</strong>
            </div>
          )}
          {/* By the time this is on screen the server has already been asked
              whether this account is a user of the application, and said no —
              so this is a real refusal, not a missing step, and it is shown as
              one. The button stays for the one case the question cannot cover:
              an account IN the application under a DIFFERENT address from the
              Twasta one signed in here. */}
          {onNeedsProjectSignIn && (
            <button
              onClick={onNeedsProjectSignIn}
              style={{
                marginTop: 6, alignSelf: 'center', padding: '6px 14px', fontSize: 13,
                borderRadius: 6, border: '1px solid var(--color-border)',
                background: 'var(--color-surface)', color: 'var(--color-text)',
                cursor: 'pointer',
              }}
            >
              Sign in to this application
            </button>
          )}
        </div>
      </FillCenter>
    )
  }

  if (loadError || !data) {
    return (
      <FillCenter>
        <div style={{ maxWidth: 360, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>
            {loadError ? 'Couldn\'t open this application' : 'Nothing to preview yet'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            {variant === 'builder'
              ? 'Tell the AI what to build in the chat — your app will appear here as it\'s created.'
              : (loadError || 'The application hasn\'t been generated yet.')}
          </div>
        </div>
      </FillCenter>
    )
  }

  const themeColor = data.application?.theme_color || 'var(--color-accent)'
  const fullNav = (data.navigation || []) as RunNavItem[]
  // Rights-filtered navigation (Run view): hide nodes whose `roles`
  // allow-list doesn't intersect the caller's functional roles and prune
  // emptied folders — the same shared filter the deployed runtime applies
  // server-side in GET /navigation, so both twins stay at parity. UX only:
  // object-level rights remain the security boundary.
  const filteredNav = variant === 'run' && runRoles !== null
    ? filterNavByRoles(fullNav, runRoles, {
      isAdmin: runSeesFullNav,
      deniedObjects: runDeniedObjects,
      grantedObjects: runGrantedObjects,
      requireGrant: runRequireGrant,
    })
    : fullNav
  // Process Overview (obj_type "L") is not an authored menu entry — it exists
  // whenever the application defines at least one Process Flow, and nowhere
  // else. Appended rather than authored so a designer never has to remember to
  // add it, and an app that draws no flows is untouched.
  const withFlows = hasFlows
    ? [...filteredNav, {
      id: PROCESS_OVERVIEW_ID,
      parent_id: data.application?.id || '',
      obj_type: 'L',
      obj_name: PROCESS_OVERVIEW_ID,
      title: 'Process overview',
      order: 9999,
    } as RunNavItem]
    : filteredNav
  // Support — the same idea for the application's support team: never
  // authored, appended for whoever the server says is on the team (the
  // endpoints behind it re-check that on every call). Everyone else reaches
  // their own issues through the top bar's Support button.
  const nav = supportMeta?.me.is_team
    ? [...withFlows, {
      id: SUPPORT_ID,
      parent_id: data.application?.id || '',
      obj_type: SUPPORT_TYPE,
      obj_name: SUPPORT_ID,
      title: 'Support',
      order: 10000,
    } as RunNavItem]
    : withFlows

  // WHICH SCREEN IS OPEN — derived the way the shell derives it, not from the
  // last click. In the Run view the URL owns the selection (`selectedObjName`
  // below), and `RunNavShell` calls `onSelect` only when somebody CLICKS a
  // menu entry: a screen reached by URL — a deep link, a bookmark, a reload, an
  // app-search hit, an Assistant citation's "Read document" — left `openItem`
  // null. That is the Assistant's document scope, so an unclicked screen made
  // it search EVERY repository in the Business Area and answer a question asked
  // inside one document library out of another one. Resolved through `nav` so a
  // URL naming a screen this application does not have (or one this caller's
  // roles hide) scopes to nothing rather than to a name nobody opened.
  const openedObjName = (onObjectChange ? initialObjName : openItem?.obj_name) || ''
  const openedItem = openedObjName
    ? nav.find((n) => n.obj_type !== '-' && n.obj_name === openedObjName)
      // Case-insensitively as a fallback only — same rule as the shell, which
      // resolves a link built from a model file against a menu entry that may
      // differ in case.
      || nav.find((n) => n.obj_type !== '-'
        && n.obj_name.toLowerCase() === openedObjName.toLowerCase())
      || null
    : null

  // Embedded in a host application: draw the object and nothing else. The
  // rights filter still applies — an object the caller's roles hide is not
  // reachable by typing its name into the host's URL either.
  const embeddedItem = embedded && initialObjName
    ? nav.find((n) => n.obj_name === initialObjName) || null
    : null
  if (embedded && embeddedItem) {
    return (
      <div style={{ height: '100%', width: '100%', minHeight: 0 }}>
        <ObjectRenderer
          key={`${embeddedItem.obj_type}-${embeddedItem.obj_name}-${reloadKey}`}
          item={embeddedItem}
          project={project}
          variant={variant}
          ownerUserId={ownerUserId}
          siteBase={siteBaseFor(nav, embeddedItem, project, ownerUserId)}
          screenState={screenState}
          onScreenStateChange={onScreenStateChange}
        />
        <AssistantLauncher project={project} objName={embeddedItem.obj_name}
                           onOpen={onOpenTarget ? openTarget : undefined} />
      </div>
    )
  }
  if (embedded && initialObjName) {
    // Named an object the app doesn't have (or one this account's roles hide).
    // Say so — falling back to the full shell would put the very chrome the
    // host asked us to drop back on screen.
    return (
      <FillCenter>
        <div style={{ maxWidth: 360, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>Screen not available</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            "{initialObjName}" isn't part of this application, or it isn't available to your roles.
          </div>
        </div>
      </FillCenter>
    )
  }

  // What an issue report says about where the reporter was: the screen open
  // NOW (the menu entry, not just a URL), the record when one is open, and the
  // version this caller runs.
  const supportContext = (): Record<string, unknown> => ({
    screen: openedItem?.title,
    obj_name: openedItem && openedItem.obj_name !== SUPPORT_ID ? openedItem.obj_name : undefined,
    obj_type: openedItem?.obj_type,
    record_id: screenState?.rec,
    app_version: supportMeta?.environment?.version || undefined,
    surface: 'run',
  })

  return (
    // The active layout publishes into the Run top bar (its menus, the Layout
    // choice, full screen) through this provider.
    <ShellChromeProvider>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0 }}>
      {/* Not the real application: QC on a fix's staging copy. Loud on purpose. */}
      {variant === 'run' && runEnv && (
        <StagingBanner
          runtime={supportRuntime}
          env={runEnv}
          environment={supportMeta?.environment?.kind === 'staging' ? supportMeta.environment : undefined}
          onExit={onExitEnv}
        />
      )}
      {/* Run view gets a global top bar with the project-specific user menu
          (account self-service + admin). The builder preview pane keeps its
          original chrome only. */}
      {variant === 'run' && ownerUserId && (
        <RunTopBar
          support={supportMeta ? {
            runtime: supportRuntime,
            meta: supportMeta,
            getContext: supportContext,
            onOpenStaging: openStaging,
            // The board is a menu entry; opening it is ordinary navigation.
            onOpenBoard: supportMeta.me.is_team && onObjectChange
              ? () => onObjectChange(SUPPORT_ID)
              : undefined,
          } : undefined}
          userId={ownerUserId}
          project={project}
          platformUser={platformUser}
          projectUser={projectUser}
          singleApp={singleApp}
          app={{
            title: data.application?.title,
            icon: data.application?.icon,
            theme_color: data.application?.theme_color,
            collect_payments: !!data.application?.collect_payments,
          }}
          onOpenSearch={onOpenTarget ? () => setSearchOpen(true) : undefined}
          // From the FULL nav, gated only by a node's own `roles` allow-list.
          // A website is not an object anybody is granted — it has its own
          // sign-in — so the deny-by-default object gate would hide every site
          // from every caller it applies to (a shop's own customer included).
          sites={(variant === 'run' && runRoles !== null
            ? filterNavByRoles(fullNav, runRoles, { isAdmin: runSeesFullNav })
            : fullNav)
            .filter((n) => n.obj_type === 'X')
            .map((n) => ({
              name: n.title,
              type: 'X',
              obj_name: n.obj_name,
              functional_specs: n.functional_specs,
            }))}
        />
      )}
      <RunNavShell
        nav={nav}
        rootParentId={data.application?.id}
        appTitle={data.application?.title || 'application'}
        themeColor={themeColor}
        compact={compact}
        // URL-driven (run) vs internal (builder) selection: only the run mode
        // wires onObjectChange, which flips the shell into controlled mode.
        selectedObjName={onObjectChange ? (initialObjName || null) : undefined}
        onSelect={handleSelect}
        // Global search finds menu entries too, so the sidebar needs no search
        // of its own where that search is mounted.
        hideNavSearch={variant === 'run' && !!onOpenTarget}
        emptyNavMessage={variant === 'builder'
          ? 'No screens yet. Ask the AI in the chat to add one — for example: "Add a Customers screen with name, email, phone."'
          : 'No navigation items defined.'}
        emptyPaneMessage={variant === 'builder'
          ? 'Pick a screen on the left to preview it. Or ask the AI in chat to add a new one.'
          : 'Pick a screen from the menu to open it.'}
        // Layouts, Pinned / Recent and Open editors are the RUN view's: the
        // builder preview keeps the classic tree and screens' own editors.
        layoutLocked={variant !== 'run'}
        editors={variant === 'run'}
        prefsTransport={shellPrefs}
        shellConfig={(data.application as { shell?: AppShellSettings } | undefined)?.shell}
        appKey={`${ownerUserId || ''}:${project}`}
        userKey={runUserKey || (platformUser?.email || platformUser?.user_id || '').toLowerCase()}
        headerExtras={variant === 'builder' ? () => (
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            title="Refresh preview — reload metadata and redeploy schema"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 26, height: 26, borderRadius: 'var(--radius-md)',
              background: 'transparent', color: 'var(--color-text-secondary)',
              border: 'none', cursor: 'pointer',
            }}
          >
            <RefreshCw size={13} />
          </button>
        ) : undefined}
        // A background copy (a second desktop window, an Open editor's
        // kept-alive instance) gets no URL in-screen state and writes none.
        renderObject={(item, ctx) => (item.obj_name === SUPPORT_ID && supportMeta ? (
          <SupportWorkspace
            runtime={supportRuntime}
            meta={supportMeta}
            notify={platformNotify}
            onOpenStaging={openStaging}
            initialIssue={ctx?.background ? null : (Number(screenState?.issue) || null)}
            initialTab={supportTabOf(ctx?.background ? undefined : screenState?.tab)}
            // The open issue rides the URL like any other in-screen state,
            // so a notification link or a shared URL lands on it.
            onIssueChange={(n) => { if (!ctx?.background) onScreenStateChange?.(n ? { issue: String(n) } : null) }}
            newIssueSource="run"
            getContext={supportContext}
          />
        ) : (
          /* Key includes reloadKey so the preview pane re-mounts after an
             agent change. This is what re-fires LivePreviewTab's deploy() →
             materialises any new columns into the preview DB and re-renders
             the form. Without this, schema changes from the agent (e.g. "add
             a column") wouldn't show up until the user manually navigated
             away and back. */
          <ObjectRenderer
            key={`${item.obj_type}-${item.obj_name}-${reloadKey}`}
            item={item}
            project={project}
            variant={variant}
            ownerUserId={ownerUserId}
            siteBase={siteBaseFor(nav, item, project, ownerUserId)}
            screenState={ctx?.background ? undefined : screenState}
            // Only the screen the URL names may write its in-screen state. A
            // screen being left (a button on its open editor just navigated
            // elsewhere) is still mounted for one more render and would
            // stamp its `mode=edit&rec=…` onto the NEW screen's URL, wiping
            // the `?open=` hand-off the new screen is about to apply.
            onScreenStateChange={ctx?.background || !onScreenStateChange ? undefined
              : (next) => {
                if (openedItem && openedItem.obj_name !== item.obj_name) return
                onScreenStateChange(next)
              }}
            onOpenTarget={onOpenTarget}
          />
        ))}
      />
      {/* Global end-user Assistant — floating chat button. Self-hides unless the
          project enabled it in Configure AI; works in both run + builder preview.
          It is told WHICH SCREEN is open: a document repository narrows the
          Assistant's document search to that repository, so a question asked
          while standing in one library is not answered from another. */}
      <AssistantLauncher project={project} objName={openedItem?.obj_name}
                         onOpen={onOpenTarget ? openTarget : undefined} />
      {/* The search panel AND the find-next bar in one mount: closing the panel
          must leave the highlight on the screen the reader opened. */}
      {variant === 'run' && onOpenTarget ? (
        <AppSearch
          open={searchOpen}
          onOpenChange={setSearchOpen}
          search={runSearch}
          onOpenHit={openHit}
          viewStorageKey={project}
          screens={searchScreens(nav)}
          onOpenScreen={(s) => {
            const item = nav.find((n) => n.id === s.id)
            if (item) handleSelect(item)
          }}
        />
      ) : null}
    </div>
    </ShellChromeProvider>
  )
}

/**
 * The menu as search entries: every screen (not folder), with the folders it
 * sits in as its "group". Menu order, so the empty-query suggestions are the
 * top of the menu.
 */
function searchScreens(nav: RunNavItem[]): SearchScreen[] {
  const byId = new Map(nav.map((n) => [n.id, n]))
  const order = (n: RunNavItem): number[] => {
    const chain: number[] = []
    let cur: RunNavItem | undefined = n
    for (let i = 0; cur && i < 50; i += 1) {
      chain.unshift(cur.order ?? 0)
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined
    }
    return chain
  }
  const groupOf = (n: RunNavItem): string => {
    const names: string[] = []
    let cur = n.parent_id ? byId.get(n.parent_id) : undefined
    for (let i = 0; cur && i < 50; i += 1) {
      names.unshift(cur.title)
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined
    }
    return names.join(' › ')
  }
  return nav
    .filter((n) => n.obj_type !== '-' && n.title)
    .map((n) => ({ n, key: order(n) }))
    .sort((a, b) => {
      for (let i = 0; i < Math.max(a.key.length, b.key.length); i += 1) {
        const d = (a.key[i] ?? -1) - (b.key[i] ?? -1)
        if (d) return d
      }
      return 0
    })
    .map(({ n }) => ({ id: n.id, obj_name: n.obj_name, title: n.title, group: groupOf(n) }))
}

function ObjectRenderer({
  item,
  project,
  variant,
  ownerUserId,
  siteBase,
  screenState,
  onScreenStateChange,
  onOpenTarget,
}: {
  item: RunNavItem
  project: string
  variant: 'run' | 'builder'
  ownerUserId?: string
  siteBase?: string
  screenState?: Record<string, string>
  onScreenStateChange?: (next: Record<string, string> | null) => void
  onOpenTarget?: (target: { objName: string; params: Record<string, string> }) => void
}) {
  const builder = variant === 'builder'
  // In Builder Mode, gate the preview on whether the object's model has been
  // generated. Until then we show its specification + a "Create" button rather
  // than an empty stub form (the LivePreview panes happily render blank forms
  // for missing models, which is confusing during a build).
  const [status, setStatus] = useState<'loading' | 'exists' | 'missing'>(
    builder ? 'loading' : 'exists',
  )

  useEffect(() => {
    if (!builder) return
    let cancelled = false
    setStatus('loading')
    checkModelStatus(project, item.obj_name, item.obj_type)
      .then((r: { exists?: boolean }) => {
        if (!cancelled) setStatus(r?.exists ? 'exists' : 'missing')
      })
      .catch(() => {
        // Treat a failed check as "not generated" — better to offer the
        // create/spec affordance than to render a blank form.
        if (!cancelled) setStatus('missing')
      })
    return () => {
      cancelled = true
    }
  }, [builder, project, item.obj_name, item.obj_type])

  // External Websites (X) carry no generated "model" — the builder gate
  // doesn't apply; render the live site preview directly.
  if (builder && item.obj_type !== 'X' && status === 'loading') {
    return (
      <FillCenter>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
        <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>Checking…</span>
      </FillCenter>
    )
  }

  if (builder && item.obj_type !== 'X' && status === 'missing') {
    return <ObjectSpecPane project={project} item={item} />
  }

  return (
    <ObjectPreview
      item={item}
      project={project}
      ownerUserId={ownerUserId}
      siteBase={siteBase}
      screenState={screenState}
      onScreenStateChange={onScreenStateChange}
      onOpenTarget={onOpenTarget}
    />
  )
}

/** Renders the live preview for an already-generated object. */
function ObjectPreview({
  item, project, ownerUserId, siteBase, screenState, onScreenStateChange, onOpenTarget,
}: {
  item: RunNavItem
  project: string
  ownerUserId?: string
  siteBase?: string
  screenState?: Record<string, string>
  onScreenStateChange?: (next: Record<string, string> | null) => void
  onOpenTarget?: (target: { objName: string; params: Record<string, string> }) => void
}) {
  // A worklist task opens its target with `?mode=add&seed=<task key>`. The
  // KEY travels, never the payload: a seed can carry a whole header plus its
  // detail lines, and the server re-authorises the caller on every fetch — so
  // a link stays short and shareable, and a task that was reassigned or
  // finished fails cleanly here instead of opening someone else's data.
  // Stable across renders: it reaches the document view through a `useMemo`,
  // and a fresh function every render would rebuild that view while somebody
  // is typing in it.
  const openFiled = useCallback(
    ({ repositoryObj, documentId }: { repositoryObj: string; documentId: string }) => {
      onOpenTarget?.({ objName: repositoryObj, params: { doc: documentId } })
    }, [onOpenTarget])

  const openSource = useCallback(
    (source: { obj_name: string; txn_id: string }) => {
      onOpenTarget?.({
        objName: source.obj_name,
        params: { mode: 'edit', rec: source.txn_id, view: 'document' },
      })
    }, [onOpenTarget])

  // A handler='open' action button on another screen navigated here with
  // `?open=<id>`: the values it resolved from its row were parked in the
  // hand-off store (never the URL), and the screen applies them on open.
  const openId = screenState?.open || ''
  const openPayload = useMemo(() => {
    const p = readOpen(openId)
    return p && p.objName === item.obj_name ? p : null
  }, [openId, item.obj_name])
  const navigateOpen = useCallback((req: OpenActionRequest) => {
    if (!onOpenTarget) return
    const id = stashOpen(openScreenPayloadOf(req))
    onOpenTarget({ objName: req.objName, params: { open: id } })
  }, [onOpenTarget])
  // A handler='wizard' action button that navigates: the wizard's menu entry,
  // its inputs on the query (the same URL a deep link / QR code uses).
  const navigateWizard = useCallback((req: WizardActionRequest) => {
    onOpenTarget?.(wizardTarget(req.objName, req.inputs))
  }, [onOpenTarget])

  const seedRef = screenState?.seed || ''
  const [taskSeed, setTaskSeed] = useState<Record<string, unknown> | undefined>(undefined)
  useEffect(() => {
    if (!seedRef) { setTaskSeed(undefined); return undefined }
    let cancelled = false
    worklistSeed(project, seedRef)
      .then((res) => { if (!cancelled) setTaskSeed(res?.seed || {}) })
      .catch(() => {
        // Opening the blank form is the right failure: the person still has
        // the work to do, they just have to key it. Saying so beats a form
        // that looks filled but is not.
        if (!cancelled) { setTaskSeed({}); toast.error('That task\u2019s data could not be loaded — the form is blank.') }
      })
    return () => { cancelled = true }
  }, [project, seedRef])

  if (item.obj_type === 'L') {
    return (
      <ProcessOverview
        project={project}
        runtime={{
          execute: (objName, action, payload) =>
            livePreviewExecute(project, objName, action, payload),
          load: (objName, txnId) => livePreviewLoad(project, objName, txnId),
          getModel: (objName) => loadModel(project, objName),
        }}
        onOpenRecord={onOpenTarget
          ? (objName, txnId) => onOpenTarget({ objName, params: { mode: 'edit', rec: txnId } })
          : undefined}
      />
    )
  }
  if (item.obj_type === 'H') {
    // A Scheduler on the menu: next run, what the job does, its history and
    // its results. The run record is Twasta's own — see `twasta_job_runs`.
    return (
      <SchedulerRunPane runtime={makeSchedulerRuntime(project)}
        objName={item.obj_name} />
    )
  }
  if (item.obj_type === 'J') {
    // An IOFlow flow on the menu. The nav node carries the flow id in its
    // functional_specs, and a MANUAL flow's input form is the transaction
    // named after the node; the server resolves both from the name, so the
    // pane is addressed by obj_name alone (docs/flow_forms.md).
    return <FlowRunPane project={project} objName={item.obj_name} />
  }
  if (item.obj_type === 'X') {
    return <SiteLivePreviewPane item={item} project={project} ownerUserId={ownerUserId} />
  }
  if (item.obj_type === 'T') {
    return (
      <LivePreviewLazyPane
        project={project}
        objName={item.obj_name}
        layoutMode="customize"
        // The Run shell mounts the work panel, so a record here can be handed
        // to somebody. The designer's live preview mounts none, and offers no
        // "Assign to…" — a button that opens a dialog whose task nobody in
        // that surface could ever see is worse than no button.
        canAssignWork
        // The Document view of a record leads to that record's filed copy.
        onOpenFiled={onOpenTarget ? openFiled : undefined}
        initialScreen={screenState ? {
          mode: screenState.mode, rec: screenState.rec,
          // `?view=document` — how "edit this document on its record" lands on
          // the document face rather than the grid form.
          view: screenState.view, seed: taskSeed,
          ...(openPayload ? { open: openPayload, openKey: openId } : {}),
        } : undefined}
        // An 'open' action button that navigates; a drawer one is handled by
        // the screen's host itself.
        onOpenObject={onOpenTarget ? navigateOpen : undefined}
        onOpenWizard={onOpenTarget ? navigateWizard : undefined}
        onScreenChange={onScreenStateChange
          ? (s) => onScreenStateChange(
              s ? { mode: s.mode || '', ...(s.rec ? { rec: s.rec } : {}) } : null,
            )
          : undefined}
      />
    )
  }
  if (item.obj_type === 'E') {
    // A Document Form on the menu. The usual door is the fourth VIEW of its
    // anchor transaction (see TxnScreen's `documentView`); this is the other
    // one, for a form whose whole purpose is "fill in one of these".
    return (
      <DocumentFormPane
        project={project}
        objName={item.obj_name}
        // Filing a governed document puts it in a `K` repository somewhere
        // else on this menu. The shell is what can go there, so it hands the
        // document the way — same target shape a search hit uses.
        onOpenFiled={onOpenTarget ? openFiled : undefined}
      />
    )
  }
  if (item.obj_type === 'U') {
    // A Transaction Wizard: the shared runner over this platform's transport.
    // Its launch inputs come from the URL (a deep link, a QR code, an action
    // button's hand-off) and a Work List task resumes a run with `?resume=`.
    return (
      <WizardPane
        project={project}
        objName={item.obj_name}
        screenState={screenState}
        openPayload={openPayload}
        onOpenTarget={onOpenTarget}
        onScreenStateChange={onScreenStateChange}
      />
    )
  }
  if (item.obj_type === 'S') {
    return <SmartPageLivePreviewPane project={project} objName={item.obj_name} siteBase={siteBase} />
  }
  if (item.obj_type === 'G') {
    // Same pane that backs the designer Live Preview — run-mode keeps the
    // design read-only for the end-user.
    return (
      <GenProcessLivePreviewPane project={project} objName={item.obj_name} runMode customizable
        key={openId || 'plain'}
        initialCriteria={openPayload?.params}
        protectedCriteria={openPayload?.protectedParams} />
    )
  }
  if (item.obj_type === 'V') {
    // The SAME pane the designer Live Preview uses — renders the saved visual
    // via the shared InsightVisual engine. Already read-only (no edit chrome),
    // so it doubles as the end-user run surface unchanged.
    return <VisualLivePreviewPane project={project} objName={item.obj_name}
      key={openId || 'plain'} initialParams={openPayload?.params} />
  }
  if (item.obj_type === 'D') {
    // Dashboard = composition of visuals; same designer Live Preview pane.
    return <DashboardLivePreviewPane project={project} objName={item.obj_name}
      key={openId || 'plain'} initialGlobals={openPayload?.params} />
  }
  if (item.obj_type === 'K') {
    // Document Repository: the SHARED page-runtime views, so the platform Run
    // surface and a deployed app render the same repository identically.
    // `?doc=<id>` opens one document directly — how a search result, or a
    // shared link, lands on the document rather than on the repository list.
    return (
      <DocumentRepositoryPane
        project={project}
        objName={item.obj_name}
        initialDocumentId={screenState?.doc || ''}
        onDocumentChange={onScreenStateChange
          ? (documentId) => onScreenStateChange(documentId ? { doc: documentId } : null)
          : undefined}
        // A document a Document Form filed is edited on its record, in the
        // document view of that record — not in the repository's own writer,
        // which would fork the wording away from the data behind it.
        onOpenSource={onOpenTarget ? openSource : undefined}
      />
    )
  }
  if (item.obj_type === 'R') {
    // Jasper report: shared ReportViewer (criteria form -> HTML preview /
    // export downloads) — same pane the designer Live Preview uses.
    return <ReportLivePreviewPane project={project} objName={item.obj_name} customizable />
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 32,
        textAlign: 'center',
        color: 'var(--color-text-secondary)',
        fontSize: 13,
      }}
    >
      <strong style={{ color: 'var(--color-text)', fontSize: 14 }}>
        {RUN_TYPE_LABEL[item.obj_type] || item.obj_type} preview not yet available
      </strong>
      <span>
        Preview currently supports Transactions, Smart Pages, Visuals,
        Dashboards, Reports, General Processes, Wizards and Document
        Repositories.
      </span>
    </div>
  )
}

/** A website's slug — mirrors the backend's resolution (spec.slug || obj_name). */
function siteSlugOf(site: RunNavItem): string {
  try {
    const spec = JSON.parse(site.functional_specs || '{}')
    if (spec.slug) return String(spec.slug)
  } catch { /* use obj_name */ }
  return site.obj_name
}

function siteViewerPath(ownerUserId: string, project: string, site: RunNavItem): string {
  return siteAddress(ownerUserId, project, siteSlugOf(site))
}

/**
 * The site viewer path of the website (X) a smart page sits under in the
 * menu, or undefined for a page that belongs to no website. Walks parent_id
 * up, guarded against a cyclic menu.
 */
function siteBaseFor(
  nav: RunNavItem[], item: RunNavItem, project: string, ownerUserId?: string,
): string | undefined {
  if (!ownerUserId || item.obj_type !== 'S') return undefined
  const byId = new Map(nav.map((n) => [n.id, n]))
  const seen = new Set<string>()
  let cur = byId.get(item.parent_id)
  while (cur && !seen.has(cur.id)) {
    if (cur.obj_type === 'X') return siteViewerPath(ownerUserId, project, cur)
    seen.add(cur.id)
    cur = byId.get(cur.parent_id)
  }
  return undefined
}

/**
 * External Website (X) live preview — renders the public site inline in an
 * iframe (the same /site viewer the published site uses), with an
 * "Open in new tab" affordance. The site URL mirrors the backend slug
 * resolution (spec.slug || obj_name).
 */
function SiteLivePreviewPane({ item, project, ownerUserId }: {
  item: RunNavItem
  project: string
  ownerUserId?: string
}) {
  if (!ownerUserId) {
    return (
      <FillCenter>
        <Globe size={18} style={{ color: 'var(--color-text-secondary)' }} />
        <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
          Site preview is unavailable here — open the website from the workbench.
        </span>
      </FillCenter>
    )
  }
  const siteSlug = siteSlugOf(item)
  const url = siteViewerPath(ownerUserId, project, item)
  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, padding: '0.5rem 0.75rem',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)', flexShrink: 0,
        }}
      >
        <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.title || siteSlug}
        </span>
        <button
          onClick={() => window.open(url, '_blank', 'noopener')}
          title="Open the site in a new tab"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '0.35rem 0.7rem', borderRadius: 'var(--radius-md)', flexShrink: 0,
            background: 'var(--color-surface)', color: 'var(--color-text)',
            border: '1px solid var(--color-border)', fontSize: '0.875rem',
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          <ExternalLink size={14} />
          Open in new tab
        </button>
      </div>
      <iframe
        key={item.obj_name}
        src={url}
        title={`Site preview: ${item.title || siteSlug}`}
        style={{ flex: 1, width: '100%', border: 'none', background: '#fff' }}
      />
    </div>
  )
}

/**
 * Shown in Builder Mode when an object exists in the app structure but its
 * model hasn't been generated yet. Displays the object's functional
 * specification (editable), a Save button that appears once edited, and a
 * Create button that hands the object off to the AI agent for generation.
 *
 * Generation is NOT a direct API call — it's dispatched to the persistent
 * Ask-AI dock (via a window event), which triggers the specific generation
 * sub-agent with the object's info and shows progress in the chat. The
 * preview then reloads automatically when the agent finishes (EndUserAppView
 * listens for `twasta:agent-files-changed`).
 */
function ObjectSpecPane({
  project,
  item,
}: {
  project: string
  item: RunNavItem
}) {
  const initialSpec = item.functional_specs?.trim() || item.description?.trim() || ''
  const [spec, setSpec] = useState(initialSpec)
  const [originalSpec, setOriginalSpec] = useState(initialSpec)
  const [saving, setSaving] = useState(false)
  const [handedOff, setHandedOff] = useState(false)

  const dirty = spec !== originalSpec
  const busy = saving || handedOff
  const Icon = RUN_TYPE_ICON[item.obj_type] || FileText
  const typeLabel = RUN_TYPE_LABEL[item.obj_type] || item.obj_type
  const createNoun = CREATE_NOUN[item.obj_type] || typeLabel

  // Persist the edited spec into application.json. Re-fetches fresh so we don't
  // clobber unrelated changes made elsewhere with a stale snapshot.
  const persistSpec = useCallback(
    async (value: string) => {
      const raw: RawAppData = await getJsonRaw(project)
      const nav = (raw.navigation || []).map((n: FlatNavItem) =>
        n.id === item.id ? { ...n, functional_specs: value } : n,
      )
      await saveJson(project, { ...raw, navigation: nav })
    },
    [project, item.id],
  )

  const handleSave = useCallback(async () => {
    if (!dirty || busy) return
    setSaving(true)
    try {
      await persistSpec(spec)
      setOriginalSpec(spec)
      toast.success('Specification saved')
    } catch {
      toast.error('Could not save the specification')
    } finally {
      setSaving(false)
    }
  }, [dirty, busy, persistSpec, spec])

  const handleCreate = useCallback(async () => {
    if (busy) return
    const specText = spec.trim()
    if (!specText) {
      toast.error('Add a specification before creating this screen')
      return
    }
    setHandedOff(true)
    // Persist the edited spec first so the agent and the saved app use the
    // same text (best-effort — the spec is also passed in the event below).
    if (dirty) {
      try {
        await persistSpec(spec)
        setOriginalSpec(spec)
      } catch {
        /* non-fatal */
      }
    }
    // Hand off to the AI agent dock, targeting the specific generation
    // sub-agent for this object. All Builder Mode AI work happens in the agent.
    window.dispatchEvent(
      new CustomEvent('twasta:agent-create-object', {
        detail: {
          project,
          objName: item.obj_name,
          objType: item.obj_type,
          title: item.title,
          description: item.description?.trim() || '',
          functionalSpecs: specText,
          designReferenceUrl: item.design_reference_url || '',
        },
      }),
    )
    toast.success(`Asked the AI agent to build ${item.title}`)
    // Re-enable after a moment; the preview reloads automatically when the
    // agent finishes (via the agent-files-changed event).
    window.setTimeout(() => setHandedOff(false), 4000)
  }, [busy, spec, dirty, persistSpec, item, project])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        maxWidth: 760,
        margin: '0 auto',
        width: '100%',
        padding: '0.5rem 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-accent)',
            flexShrink: 0,
          }}
        >
          <Icon size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0, color: 'var(--color-text)' }}>
              {item.title}
            </h2>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                padding: '2px 8px',
                borderRadius: 999,
                background: 'rgba(255,152,0,0.12)',
                color: '#b26a00',
                border: '1px solid rgba(255,152,0,0.3)',
              }}
            >
              Not built yet
            </span>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            {typeLabel} · review or edit the specification, then create it. The screen will appear here once it's built.
          </p>
        </div>
      </div>

      <div>
        <label
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            marginBottom: 6,
          }}
        >
          Specification
        </label>
        <textarea
          value={spec}
          onChange={(e) => setSpec(e.target.value)}
          disabled={busy}
          placeholder="Describe what this screen should capture and do — its fields, actions, and rules."
          style={{
            width: '100%',
            minHeight: 240,
            padding: '0.875rem',
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            resize: 'vertical',
            fontFamily: 'var(--font-primary)',
          }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleCreate}
          disabled={busy || !spec.trim()}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {handedOff ? (
            <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Wand2 size={15} />
          )}
          {handedOff ? 'Building…' : `Create ${createNoun}`}
        </button>

        {dirty && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleSave}
            disabled={busy}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {saving ? (
              <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Save size={15} />
            )}
            {saving ? 'Saving…' : 'Save specification'}
          </button>
        )}

        {dirty && !saving && !handedOff && (
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Unsaved changes</span>
        )}
        {handedOff && (
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Building in the AI agent — watch the chat below.
          </span>
        )}
      </div>
    </div>
  )
}

function FillCenter({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
      }}
    >
      {children}
    </div>
  )
}

